import React, { useState } from 'react';
import axios from 'axios';
import './TicketTableDashboard.css';
import './TicketTableArchive.css';
import './UI/SearchInput.css';
import { useTranslation } from 'react-i18next';
import {
  RESOLUTION_ACTOR_VALUES,
  TERMINAL_STATUS_VALUES,
  getResolutionActor,
  getResolutionActorLabel,
  getStatusLabel,
} from '../constants/ticketStatuses';
import { FaInstagram, FaFacebookMessenger, FaWhatsapp, FaEnvelope, FaGlobe, FaSort, FaSortUp, FaSortDown, FaSearch } from 'react-icons/fa';

const TicketTableArchive = ({
  tickets,
  user,
  onTicketClick,
  pagination,
  onPageChange,
  statusFilter,
  resolvedByFilter,
  nonRequestsFilter = false,
  onFilterChange,
}) => {
  const { t } = useTranslation();
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const channelIcons = {
    Instagram: FaInstagram,
    Messenger: FaFacebookMessenger,
    WhatsApp: FaWhatsapp,
    Gmail: FaEnvelope,
    Outlook: FaEnvelope,
    Website: FaGlobe,
    Custom: FaEnvelope
  };

  const statusOptions = TERMINAL_STATUS_VALUES;

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    };
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcDateString).toLocaleDateString('fr-FR', options);
  };

  const sortTickets = (tickets, field, order) => {
    return [...tickets].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (field === 'created_at') {
        return order === 'asc' ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
      }
      if (field === 'sentiment_score') {
        return order === 'asc' ? (valA || 0) - (valB || 0) : (valB || 0) - (valA || 0);
      }
      return order === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });
  };

  const filterTickets = (tickets) => {
    return tickets.filter(ticket => {
      const matchesStatus = statusFilter ? ticket.status === statusFilter : true;
      const matchesResolvedBy = resolvedByFilter
        ? getResolutionActor(ticket) === resolvedByFilter
        : true;
      return matchesStatus && matchesResolvedBy;
    });
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setIsSearching(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/tickets/search/', {
        params: { 
          query: searchQuery,
          sort_field: sortField,
          sort_order: sortOrder,
          terminal_only: true,
          status: statusFilter || undefined,
          resolved_by: resolvedByFilter || undefined,
          non_requests: nonRequestsFilter ? true : undefined,
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching tickets:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim() === '') {
      setSearchResults(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleSortChange = (field) => {
    setSortField(field);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    if (searchResults) {
      handleSearch();
    }
  };

  const handleStatusFilterChange = (e) => {
    onFilterChange({ status: e.target.value, resolvedBy: resolvedByFilter, nonRequests: nonRequestsFilter });
    setSearchResults(null);
  };

  const handleResolvedByFilterChange = (e) => {
    onFilterChange({ status: statusFilter, resolvedBy: e.target.value, nonRequests: nonRequestsFilter });
    setSearchResults(null);
  };

  // KI-282: the e-mails the AI closed as « not a request » are left out of the
  // archive; this select lists them alone.
  const handleKindFilterChange = (e) => {
    onFilterChange({ status: statusFilter, resolvedBy: resolvedByFilter, nonRequests: e.target.value === 'non_requests' });
    setSearchResults(null);
  };

  const getSentimentScoreClass = (score) => {
    if (score === null || score === undefined) return 'null';
    if (score <= 25) return 'low';
    if (score <= 60) return 'medium';
    return 'high';
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FaSort />;
    return sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const displayedTickets = searchResults !== null ? searchResults : tickets;
  const sortedAndFilteredTickets = sortTickets(filterTickets(displayedTickets), sortField, sortOrder);

  const handlePageChangeInternal = (direction) => {
    if (!onPageChange || !pagination) return;
    if (direction === 'next' && pagination.has_next) {
      onPageChange(pagination.current_page + 1);
    } else if (direction === 'prev' && pagination.has_previous) {
      onPageChange(Math.max(1, pagination.current_page - 1));
    }
  };

  return (
    <div className="ticket-table-dashboard">
      <div className="table-controls">
        <div className="filters-container">
          <div className="status-filter">
            <select
              id="status-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="">{t('all')}</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {getStatusLabel(t, status)}
                </option>
              ))}
            </select>
          </div>
          
          <div className="status-filter">
            <select
              id="resolved-by-select"
              value={resolvedByFilter}
              onChange={handleResolvedByFilterChange}
              title={t('ticketTableArchive.resolvedByFilter', 'Resolved by')}
            >
              <option value="">{t('ticketTableArchive.resolvedByAnyone', 'Resolved by anyone')}</option>
              {RESOLUTION_ACTOR_VALUES.map((actor) => (
                <option key={actor} value={actor}>
                  {t(`resolutionActors.${actor}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="status-filter">
            <select
              id="ticket-kind-select"
              value={nonRequestsFilter ? 'non_requests' : 'tickets'}
              onChange={handleKindFilterChange}
              title={t('ticketTableArchive.kindFilter', 'Ticket type')}
            >
              <option value="tickets">{t('ticketTableArchive.kindTickets', 'Tickets')}</option>
              <option value="non_requests">{t('ticketTableArchive.kindNonRequests', 'Not requests')}</option>
            </select>
          </div>

          <div className="company-filter">
            <div className="search-container">
              <input
                id="search-input"
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyPress={handleKeyPress}
                placeholder={t('searchGeneral', 'Search across all fields...')}
                className="search-input"
              />
              <button
                onClick={handleSearch}
                className="search-submit-button"
                title={t('search', 'Search')}
                disabled={isSearching}
              >
                <FaSearch />
              </button>
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="clear-search-button"
                  title={t('clearSearch', 'Clear search')}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
        {pagination && (
          <div className="pagination">
            <button 
              onClick={() => handlePageChangeInternal('prev')} 
              disabled={!pagination.has_previous}
            >
              {t('common.previous')}
            </button>
            <span>
              {pagination.current_page || 1} / {pagination.total_pages || 1}
            </span>
            <button 
              onClick={() => handlePageChangeInternal('next')} 
              disabled={!pagination.has_next}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      <table>
        <thead>
          <tr>
            <th>{t('channel')}</th>
            <th>{t('subject')}</th>
            <th 
              onClick={() => handleSortChange('sentiment_score')} 
              className="sortable"
            >
              {t('sentimentScore')} <SortIcon field="sentiment_score" />
            </th>
            <th>{t('carrier')}</th>
            <th>{t('company')}</th>
            <th>{t('status')}</th>
            <th>{t('ticketTableArchive.resolvedBy', 'Resolved by')}</th>
            <th 
              onClick={() => handleSortChange('created_at')} 
              className="sortable"
            >
              {t('createdAt')} <SortIcon field="created_at" />
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredTickets.length > 0 ? (
            sortedAndFilteredTickets.map((ticket) => {
              const ChannelIcon = channelIcons[ticket.channel_name];
              const statusLabel = getStatusLabel(t, ticket.status);
              const resolvedByLabel = getResolutionActorLabel(t, ticket);
              return (
                <tr key={ticket._id} onClick={() => onTicketClick(ticket._id)}>
                  <td className="tertiary-info">
                    <div className="visual-indicator">
                      {ChannelIcon && <ChannelIcon className="channel-icon-dashboard" />}
                      <span>{ticket.channel_name || t('unknown', 'Unknown')}</span>
                    </div>
                  </td>
                  <td className="primary-info">
                    <div className="subject-cell">
                      <span className="subject-text truncate">{ticket.subject}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`sentiment-score ${getSentimentScoreClass(ticket.sentiment_score)}`}>
                      {ticket.sentiment_score !== null && ticket.sentiment_score !== undefined 
                        ? Math.round(ticket.sentiment_score) 
                        : t('ticketTableArchive.notAvailable', 'N/A')}
                    </span>
                  </td>
                  <td className="secondary-info">{ticket.carrier_name || t('unknown')}</td>
                  <td className="secondary-info">{ticket.company_name || t('unknown')}</td>
                  <td className="centered">
                    {ticket.non_request ? (
                      <span
                        className="non-request-badge"
                        title={t('ticketTableArchive.nonRequestTitle', {
                          reason: ticket.non_request.reason || '',
                          defaultValue: 'Closed by the AI: not a request — {{reason}}',
                        })}
                      >
                        {t(`ticketTableArchive.nonRequestCategories.${ticket.non_request.category}`,
                          ticket.non_request.label || ticket.non_request.category)}
                      </span>
                    ) : (
                      <span className={`status-badge ${ticket.status}`} title={statusLabel}>
                        {statusLabel}
                      </span>
                    )}
                  </td>
                  <td className="centered">
                    {resolvedByLabel ? (
                      <span className={`resolved-by-badge ${getResolutionActor(ticket)}`}>
                        {resolvedByLabel}
                      </span>
                    ) : (
                      <span className="resolved-by-unknown">—</span>
                    )}
                  </td>
                  <td className="tertiary-info date-info">{formatDate(ticket.created_at)}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="8" style={{ textAlign: 'center', padding: '24px', color: 'rgba(255,255,255,0.5)' }}>
                {searchResults !== null && searchResults.length === 0
                  ? t('ticketTableArchive.noMatchingTickets', 'No matching tickets found')
                  : t('ticketTableArchive.noTicketsAvailable', 'No archived tickets available')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TicketTableArchive;
